import clsx from 'clsx'
import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useFormik } from 'formik'
import { Alert, Box, Button, Link, TextField } from '@mui/material'

import { getErrorProp, getHelperTextProp } from '../../../common/formProps'
import useTranslations from '../../../i18n/useTranslations'

import useStyles from '../styles'

import contactDataValidationSchema from './validationSchema'

const propTypes = {
  contactValues: PropTypes.shape({
    contactPerson: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
  updateContactData: PropTypes.func.isRequired,
}
const ContactDataEditorView = ({ contactValues, updateContactData }) => {
  const t = useTranslations()
  const classes = useStyles()

  const [isEditingEnabled, enableEditing] = useState(false)
  const [hasBeenSuccessfullyEdited, setHasBeenSuccessfullyEdited] =
    useState(false)

  const initialValues = useMemo(
    () => ({
      contactPerson: contactValues?.contactPerson ?? '',
      phone: contactValues?.phone ?? '',
    }),
    [contactValues?.contactPerson, contactValues?.phone],
  )

  const { touched, errors, ...formik } = useFormik({
    validationSchema: contactDataValidationSchema,
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: values => {
      enableEditing(false)

      updateContactData(values)

      setHasBeenSuccessfullyEdited(true)
      formik.resetForm()
      return
    },
  })

  return (
    <form
      onSubmit={formik.handleSubmit}
      onReset={e => {
        enableEditing(false)
        formik.handleReset(e)
      }}
    >
      <TextField
        label={t('user-zone:contact-person')}
        id="contactPerson"
        className={clsx('input-medium', classes.fieldTextPerson)}
        error={getErrorProp('contactPerson', errors, touched)}
        helperText={getHelperTextProp('contactPerson', errors, touched, t)}
        InputProps={{
          ...formik.getFieldProps('contactPerson'),
          readOnly: isEditingEnabled ? false : true,
        }}
      />
      <Box className={classes.fieldTextSecondaryContainer}>
        <TextField
          label={t('noun:email')}
          id="email"
          className="input-medium"
          InputProps={{
            value: contactValues.email ?? '',
            readOnly: true,
          }}
        />
        <TextField
          label={t('noun:contact-phone')}
          id="phone"
          className="input-smaller"
          error={getErrorProp('phone', errors, touched)}
          helperText={getHelperTextProp('phone', errors, touched, t)}
          InputProps={{
            ...formik.getFieldProps('phone'),
            readOnly: isEditingEnabled ? false : true,
          }}
        />
      </Box>
      {isEditingEnabled ? (
        <Box className={classes.changePasswordActionButtons}>
          <Button type="reset" variant="outlined" size="small">
            {t('action:cancel')}
          </Button>

          <Button type="submit" size="small" disabled={!formik.isValid}>
            {t('action:save')}
          </Button>
        </Box>
      ) : (
        <Box className={classes.currentPasswordContainer}>
          <Link
            component="button"
            onClick={() => {
              enableEditing(true)
              setHasBeenSuccessfullyEdited(false)
            }}
          >
            {t('user-zone:change-contact-data')}
          </Link>
          {hasBeenSuccessfullyEdited ? (
            <Alert severity="success" className={classes.alertSuccess}>
              {t('user-zone:contact-data-saved')}
            </Alert>
          ) : null}
        </Box>
      )}
    </form>
  )
}
ContactDataEditorView.propTypes = propTypes

export default ContactDataEditorView
